import { PageContainer } from "@/components/container/page-container";
import { SmileIcon } from "@/components/icons/smile-icon";
import { BurgerImage } from "@/components/images/burger-image";
import { CupCakeImage } from "@/components/images/cup-cake-image";
import { NoodleImage } from "@/components/images/noodle-image";
import { AddFoodOptionsList } from "@/components/list/add-food-options-list";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { DEFAULT_AUTHENTICATED_ROUTE } from "@/constants/app-routes";
import { QueryKeys } from "@/constants/query-keys";
import { OnboardingRoundBackground } from "@/features/user-profile/components/onboarding-round-background";
import { useCreatePlan } from "@/hooks/requests/plan/use-create-plan";
import { useMarkOnboardCompleted } from "@/hooks/requests/user/use-mark-onboard-completed";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

function OnboardingPersonalFoods() {
  const navigation = useIonRouter();
  const { showError } = useToast();
  const { updatePlanAsync } = useCreatePlan();
  const { markOnboardCompletedAsync, isPending } = useMarkOnboardCompleted();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const handleGotIt = async () => {
    setIsLoading(true);
    try {
      await markOnboardCompletedAsync();
      await updatePlanAsync();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeys.USER] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.SUBSCRIPTION_STATUS] }),
      ]);
      setSmartlookOnboardingEvent({
        type: "completed",
      });
      setMixpanelOnboardingEvent({
        type: "completed",
      });
      navigation.push(DEFAULT_AUTHENTICATED_ROUTE, "forward");
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer showToolbar={false}>
      <OnboardingRoundBackground
        classNames={{
          container: "bg-screen-gs-1",
          header: "flex flex-col justify-center items-center",
          content: "w-full flex flex-col justify-end items-center pt-8 pb-12 gap-25 !z-[100]",
        }}
        header={
          <div className="-mr-39 ml-8 flex items-center justify-center pt-12">
            <div className="flex w-60 flex-col items-center justify-center">
              <div className="flex h-13 w-full gap-4 pl-9">
                <NoodleImage className="h-9 w-9 self-end object-contain" />
                <BurgerImage className="h-9 w-9 self-start object-contain" />
                <CupCakeImage className="h-9 w-9 self-end object-contain" />
              </div>

              <div className={cn(textVariants.headline, "w-full pt-10 !text-left text-white")}>
                <h1>Bring your favourite</h1>
                <h1>foods to Nutricoach</h1>
              </div>
              <h3 className={cn(textVariants.title3, "pt-4.5 text-left text-white")}>
                Add your personal foods because nobody else is like you!
              </h3>
            </div>

            <SmileIcon className="h-65 w-65" />
          </div>
        }
      >
        <AddFoodOptionsList />

        <div className="w-full max-w-[322px]">
          <FilledButton
            color="black"
            textCase="uppercase"
            onClick={handleGotIt}
            disabled={isLoading || isPending}
          >
            {isLoading ? "Loading..." : "Got it"}
          </FilledButton>
        </div>
      </OnboardingRoundBackground>
    </PageContainer>
  );
}

export default OnboardingPersonalFoods;
